import { parametric, pathSubscription, subscription } from 'nike-re-framejs';
import { concatWithoutDuplicates, orderedIndexToIdsList } from '../indexingUtils';
import { List, Map, Set } from 'immutable';
import { isBetradarWidgetAvailable } from '../../../match-statistics/utils';
import { localizeMap } from '../../utils';
import { allVisibleMatches$, filterRadar$, filterStream$ } from '../../subs';
import { isRadarStream, isVideoStream } from './handlers';

/**
 * Sport subs
 */

export const snapshotLoaded$$ = sportName =>
    pathSubscription(['turboLive', 'overview', sportName, 'snapshotLoaded'], false);

export const sportsSnapshotLoaded$ = pathSubscription(['turboLive', 'sportsSnapshotLoaded']);

export const sports$ = subscription(
    (sports: Map<string, any>, stream: boolean, radar: boolean) => {
        if (stream && radar) {
            return sports?.filter(sport => sport.get('matchWithRadarAndVideoCount') > 0);
        } else if (stream) {
            return sports?.filter(sport => sport.get('matchWithVideoCount') > 0);
        } else if (radar) {
            return sports?.filter(sport => sport.get('matchWithRadarCount') > 0);
        }
        return sports;
    },
    pathSubscription(['turboLive', 'sports'], Map()),
    pathSubscription(['turboLive', 'filter', 'stream'], false),
    pathSubscription(['turboLive', 'filter', 'radar'], false)
);
export const sportIdsOrdered$ = subscription(orderedIndexToIdsList, pathSubscription(['turboLive', 'sportIdsByOrder']));
export const sportCodesOrdered$ = subscription(
    (sportIds: List<string>, sportsById: Map<string, any>) => {
        if (!sportIds?.size || !sportsById?.size) {
            return List();
        }
        return sportIds.map(id => sportsById.getIn([id, 'slug'], '').substring(1)).filter(i => i !== '');
    },
    sportIdsOrdered$,
    sports$
);

export const initSport$ = subscription(activeSports => {
    if (!activeSports?.size) {
        return undefined;
    }
    return activeSports.get(0);
}, sportCodesOrdered$);

export const noSportAvailable$ = subscription((sports: Map<string, any>) => sports && sports.isEmpty(), sports$);

export const sportBySportIdFiltered$$ = parametric((sportId: string) =>
    subscription(sports => sports.get(sportId), sports$)
);
export const sportBySportIdAll$$ = sportId => pathSubscription(['turboLive', 'sports', sportId], Map());
export const scoreboardType$$ = scoreboardTypeId =>
    pathSubscription(['turboLive', 'scoreboardTypes', scoreboardTypeId]);

export const allScoreboardTypes$ = pathSubscription(['turboLive', 'scoreboardTypes'], false);

export const sportByName$$ = parametric(sportCode =>
    subscription(sports => sports?.find(sport => sport.get('slug') === '/' + sportCode), sports$)
);

export const allVisibleSportMatches$$ = parametric(sportCode =>
    subscription(
        (allMatchIds: Map<string, any>, sport: Map<string, any>) => {
            const sportId = sport?.get('id');
            return allMatchIds?.size ? allMatchIds.map(m => Map(m)).filter(m => m.get('sportId') === sportId) : Map();
        },
        allVisibleMatches$,
        sportByName$$(sportCode)
    )
);

export const matchesCountBySportName$$ = parametric(sportName =>
    subscription(
        (sport, stream, radar) => {
            if (stream && radar) {
                return sport?.get('matchWithRadarAndVideoCount') || 0;
            } else if (stream) {
                return sport?.get('matchWithVideoCount') || 0;
            } else if (radar) {
                return sport?.get('matchWithRadarCount') || 0;
            }
            return sport?.get('matchCount') || 0;
        },
        sportByName$$(sportName),
        filterStream$,
        filterRadar$
    )
);

export const noMatchesForSport$ = parametric(sportName =>
    subscription(
        (matchesCount, isSnapshotLoaded) => isSnapshotLoaded && (!matchesCount || matchesCount === 0),
        matchesCountBySportName$$(sportName),
        snapshotLoaded$$(sportName)
    )
);

export const sportTitleByName$$ = parametric(sportCode =>
    subscription(sport => localizeMap(sport?.get('sportName')), sportByName$$(sportCode))
);

export const sportSwitchNotAllowedMarkets$$ = parametric(sportCode =>
    subscription(
        sport =>
            sport
                ?.get('overviewColumns')
                .filter(market => market.get('switchAllowed') === false)
                .map(marketId => marketId.get('marketId')),
        sportByName$$(sportCode)
    )
);

export const isSportSwitchAllowedMarket$$ = parametric((sportCode, columnId) =>
    subscription(sport => {
        return sport?.getIn(['overviewColumns', columnId, 'switchAllowed']);
    }, sportByName$$(sportCode))
);

const sportDefaultMarkets$$ = parametric(sportCode =>
    subscription(sport => sport?.get('overviewColumns').map(column => column.get('marketId')), sportByName$$(sportCode))
);

export const firstSportDefaultMarket$$ = parametric(sportCode =>
    subscription(sportDefaultMarkets => sportDefaultMarkets?.first(), sportDefaultMarkets$$(sportCode))
);

export const sportLayout$$ = parametric(sportCode =>
    subscription(sport => sport?.get('layout'), sportByName$$(sportCode))
);

export const sportMaxColumns$$ = parametric(sportCode =>
    subscription(sport => sport?.get('maxColumns'), sportByName$$(sportCode))
);

const sportSelectedMarkets$$ = sportCode => pathSubscription(['turboLive', 'selectedMarketIds', sportCode]);

const sportFavouriteMarkets$$ = parametric(sportCode =>
    subscription(
        (sport, favouriteOverviewMarkets) => {
            return favouriteOverviewMarkets?.get(sport?.get('id'));
        },
        sportByName$$(sportCode),
        pathSubscription(['account', 'userFavourites', 'turboLive', 'overviewMarkets'])
    )
);

export const selectedMarketIds$$ = parametric(sportCode =>
    subscription(
        ([defaultMarketIds, selectedMarketIds, favouriteMarketIds]) =>
            selectedMarketIds || concatWithoutDuplicates(favouriteMarketIds, defaultMarketIds),
        [sportDefaultMarkets$$(sportCode), sportSelectedMarkets$$(sportCode), sportFavouriteMarkets$$(sportCode)]
    )
);

export const hasMarketBet$$ = (sportCode, matchId, marketId) =>
    pathSubscription(['turboLive', 'overview', sportCode, 'betIdsByMarketAndMatch', marketId, matchId]);

export const isMarketSaveActive$$ = parametric((sportCode, marketsCols) =>
    subscription(
        ([defaultMarketIds, selectedMarketIds, favouriteMarketIds]) => {
            return (
                selectedMarketIds &&
                !selectedMarketIds
                    .take(marketsCols)
                    .equals(defaultMarketIds?.merge(favouriteMarketIds)?.take(marketsCols))
            );
        },
        [sportDefaultMarkets$$(sportCode), sportSelectedMarkets$$(sportCode), sportFavouriteMarkets$$(sportCode)]
    )
);

/**
 * Market subs
 */

const overviewMarkets$$ = sportCode => pathSubscription(['turboLive', 'overview', sportCode, 'markets'], List());

export const overviewMarket$$ = (sportCode, marketId) =>
    pathSubscription(['turboLive', 'overview', sportCode, 'markets', marketId]);

export const overviewMarketsOrdered$$ = parametric(sportCode =>
    subscription(
        overviewMarkets => overviewMarkets?.sortBy(market => market.get('order')),
        overviewMarkets$$(sportCode)
    )
);

/**
 * Tournament subs
 */

export const tournamentIds$$ = parametric(sportCode =>
    subscription(orderedIndexToIdsList, pathSubscription(['turboLive', 'overview', sportCode, 'tournamentIdsByOrder']))
);

export const tournament$$ = (sportCode, tournamentId) =>
    pathSubscription(['turboLive', 'overview', sportCode, 'tournaments', tournamentId]);

export const tournamentMatchIds$$ = parametric((sportCode, tournamentId) =>
    subscription(
        orderedIndexToIdsList,
        pathSubscription(['turboLive', 'overview', sportCode, 'matchIdsByTournament', tournamentId])
    )
);

/**
 * Match subs
 */

export const hasSportActiveMatches$$ = parametric(sportCode =>
    subscription(ids => ids && !ids.isEmpty(), matchIdsSortByTime$$(sportCode))
);

export const hasTournamentActiveMatches$$ = parametric((sportCode, tournamentId) =>
    subscription(ids => ids && !ids.isEmpty(), tournamentMatchIds$$(sportCode, tournamentId))
);

export const matchIdsSortByTime$$ = parametric(sportCode =>
    subscription(orderedIndexToIdsList, pathSubscription(['turboLive', 'overview', sportCode, 'matchIdsByStartTime']))
);

export const firstMatchSortByTime$$ = sportCode =>
    subscription(sortedIds => sortedIds?.get(0), matchIdsSortByTime$$(sportCode));

export const firstMatchSortByTournament$$ = (sportCode, tournamentId) =>
    subscription(sortedIds => sortedIds?.get(0), tournamentMatchIds$$(sportCode, tournamentId));

export const tournamentIdsWithStream$$ = parametric(sportCode =>
    subscription(
        matches =>
            matches
                ?.filter(match => {
                    return isVideoStream(match);
                })
                .map(match => match?.get('tournamentId'))
                .toSet() || Set(),
        matches$(sportCode)
    )
);

export const tournamentIdsWithRadar$$ = parametric(sportCode =>
    subscription(
        matches =>
            matches
                ?.filter(match => {
                    return isRadarStream(match) && isBetradarWidgetAvailable(match);
                })
                .map(match => match?.get('tournamentId'))
                .toSet() || Set(),
        matches$(sportCode)
    )
);

export const isTournamentVisible = (
    tournamentIdsWithStream,
    tournamentIdsWithRadar,
    filterStream,
    filterRadar,
    tournamentId
) =>
    (!filterStream || tournamentIdsWithStream?.has(tournamentId)) &&
    (!filterRadar || tournamentIdsWithRadar?.has(tournamentId));

export const showTournament$$ = parametric((sportCode, tournamentId) =>
    subscription(
        (tournamentIdsWithStream, tournamentIdsWithRadar, filterStream, filterRadar) =>
            isTournamentVisible(
                tournamentIdsWithStream,
                tournamentIdsWithRadar,
                filterStream,
                filterRadar,
                tournamentId
            ),
        tournamentIdsWithStream$$(sportCode),
        tournamentIdsWithRadar$$(sportCode),
        filterStream$,
        filterRadar$
    )
);

export const isMatchVisible = (match, filterStream, filterRadar) => {
    if (!match) {
        return false;
    }

    return (
        (!filterStream || isVideoStream(match)) &&
        (!filterRadar || (isRadarStream(match) && isBetradarWidgetAvailable(match)))
    );
};

export const showMatch$$ = parametric((sportCode, matchId) =>
    subscription(
        (match, filterStream, filterRadar) => isMatchVisible(match, filterStream, filterRadar),
        match$$(sportCode, matchId),
        filterStream$,
        filterRadar$
    )
);

export const match$$ = parametric((sportCode, matchId) =>
    subscription(
        (tlvMatch, hybridMatch, promoLiveMatch) => tlvMatch || hybridMatch || promoLiveMatch,
        pathSubscription(['turboLive', 'overview', sportCode, 'matches', matchId]),
        pathSubscription(['turboLive', 'overview', 'matches', matchId]),
        pathSubscription(['promo', 'live', 'matches', matchId])
    )
);

export const matches$ = sportCode => pathSubscription(['turboLive', 'overview', sportCode, 'matches']);

export const noFilteredMatchesCount$$ = parametric(sportCode =>
    subscription(matches => matches?.size || null, matches$(sportCode))
);

export const matchName$$ = parametric((sportCode, matchId) =>
    subscription(
        match => [match?.get('home'), match?.get('away')].filter(Boolean).join(' - '),
        match$$(sportCode, matchId)
    )
);

export const matchServe$$ = (matchId: string, sportCode: string) =>
    pathSubscription(['turboLive', 'overview', sportCode, 'matches', matchId, 'score', 'server'], '');

export const matchGame$$ = (matchId: string, sportCode: string) =>
    pathSubscription(['turboLive', 'overview', sportCode, 'matches', `${matchId}`, 'score', 'scores', 'GAME']);

export const matchPeriod$$ = (matchId: string, sportCode: string) =>
    pathSubscription(['turboLive', 'overview', sportCode, 'matches', `${matchId}`, 'score', 'scores', 'PERIOD']);

export const matchTotalScore$$ = (matchId: string, sportCode: string) =>
    pathSubscription(['turboLive', 'overview', sportCode, 'matches', `${matchId}`, 'score', 'scores', 'TOTAL']);

export const matchOpponets$$ = parametric((matchId: string, sportCode: string) =>
    subscription(
        match => (match ? [localizeMap(match.get('home')), localizeMap(match.get('away'))] : ['', '']),
        match$$(sportCode, matchId)
    )
);

/**
 * Bet subs
 */

export const overviewBet$$ = parametric((sportCode, matchId, marketId) =>
    subscription(([bets, betId]) => bets?.get(betId), [
        pathSubscription(['turboLive', 'overview', sportCode, 'bets']),
        pathSubscription(['turboLive', 'overview', sportCode, 'betIdsByMarketAndMatch', marketId, matchId])
    ])
);

export const getTournamentName$$ = (sportName, tournamentId) =>
    pathSubscription(['turboLive', 'overview', sportName, 'tournaments', tournamentId, 'tournamentName']);

export const getInfocentreSportCode$ = pathSubscription(['infocentrum', 'detail', 'sportCode']);

export const getInfocentreEventId$ = pathSubscription(['infocentrum', 'detail', 'eventId']);

const infocentreActiveTab$ = pathSubscription(['infocentrum', 'detail', 'streamTab']);

export const activeInfocentreMatchIdTab$ = parametric((matchId: string | number, tabName: string = 'matchtracker') =>
    subscription(
        (infocentreMatchId: string | number, tab: string) =>
            matchId && `${infocentreMatchId}` === `${matchId}` && tab === tabName,
        getInfocentreEventId$,
        infocentreActiveTab$
    )
);

export const getInfocentreStreamId$ = pathSubscription(['infocentrum', 'detail', 'betradarStreamId']);

export const getUfcEventId$ = subscription(db => db.getIn(['infocentrum', 'detail', 'ufcEventId']));
